<script setup lang="ts">
	import { computed, ref } from "vue";
	import type { SingleMachineField } from "@shared/types";
	import {
		type ImplementV2Model,
		useFlightpathServiceHttp,
	} from "@ptx-autonomous/libs/flightpath-service-api";
	import { PolygonThumbnail } from "@ptx-autonomous/libs/maplibre";
	import { Area } from "@ptx-autonomous/libs/units";
	import { area as turfArea } from "@turf/area";
	import Dialog from "@/components/Dialog.vue";
	import { useFarmDeleteMutation } from "@shared/composables/farms";
	import DeleteIcon from "@/icons/DeleteIcon.vue";
	import CreateField from "./CreateField.vue";
	import ButtonPrimary from "../ButtonPrimary.vue";

	const { allFields, isLoadingFields, selectedImplement, selectedField } =
		defineProps<{
			allFields: SingleMachineField[];
			isLoadingFields: boolean;
			selectedImplement: ImplementV2Model;
			selectedField?: SingleMachineField;
			isCreatingField: boolean;
		}>();

	const emits = defineEmits<{
		resetOmnibar: [void];
		backToSelectImplement: [void];
		handleFieldClick: [field: SingleMachineField];
		fieldCreationModeChanged: [isCreating: boolean];
		clearFieldSelection: [void];
	}>();

	const fieldsWithAreas = computed(() => {
		if (!allFields) return [];
		return allFields.map(field => ({
			...field,
			calculatedArea: (() => {
				try {
					const areaInM2 = turfArea(field.fieldBoundary);
					const areaInAcres = new Area(areaInM2, "m2").acre();
					return areaInAcres.toFixed(1);
				} catch (error) {
					return "0.0";
				}
			})(),
		}));
	});

	const deleteDialog = ref<InstanceType<typeof Dialog> | null>(null);
	const flightPathServiceHttp = useFlightpathServiceHttp();
	const farmDeleteMutation = useFarmDeleteMutation(flightPathServiceHttp);
	const errorMessage = ref<string>("");

	function startFieldCreation() {
		emits("clearFieldSelection");
		emits("fieldCreationModeChanged", true);
	}

	const fieldToDelete = ref<SingleMachineField | null>(null);

	function showDeleteDialog(field: SingleMachineField) {
		fieldToDelete.value = field;
		deleteDialog.value?.show();
	}

	function handleCancelDelete() {
		fieldToDelete.value = null;
		errorMessage.value = "";
	}

	async function handleConfirmDelete() {
		if (!fieldToDelete.value) return;

		try {
			await farmDeleteMutation.mutateAsync(fieldToDelete.value.farmId);
			emits("clearFieldSelection");
			deleteDialog.value?.hide();
			fieldToDelete.value = null;
			errorMessage.value = "";
		} catch (error) {
			console.error("Failed to delete field:", error);
			errorMessage.value =
				error instanceof Error
					? error.message
					: "An unexpected error occurred while deleting the field.";
		}
	}
</script>

<template>
	<div class="flex flex-col h-full">
		<div
			class="flex items-center justify-between p-4 bg-green-50 border-b border-green-200 flex-shrink-0"
		>
			<div class="flex items-center gap-3">
				<button
					@click="emits('backToSelectImplement')"
					class="text-grey-400 hover:text-grey-600 p-1"
				>
					<svg
						class="!w-6 !h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 19l-7-7 7-7"
						></path>
					</svg>
				</button>
				<div>
					<h3 class="title text-green-800 text-lg font-semibold">
						{{ isCreatingField ? "Create Field" : "Select Field" }}
					</h3>
					<div class="text-sm text-grey-600">
						{{
							isCreatingField
								? "Draw field boundary"
								: `Implement: ${selectedImplement?.ImplementName}`
						}}
					</div>
					<div
						v-if="selectedField"
						class="text-sm text-green-600 mt-1"
					>
						{{ selectedField.fieldName }} previewed • Click again to
						confirm
					</div>
				</div>
			</div>
			<button
				@click="emits('resetOmnibar')"
				class="text-grey-400 hover:text-grey-600"
			>
				<svg
					class="!w-6 !h-6"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M6 18L18 6M6 6l12 12"
					></path>
				</svg>
			</button>
		</div>

		<div
			ref="fieldListContainer"
			class="flex-1 overflow-y-auto space-y-2 p-4"
		>
			<ButtonPrimary
				@click="startFieldCreation"
				v-if="!isCreatingField"
				class="w-full text-left flex items-center justify-center gap-2 px-4 py-2 mb-2"
			>
				<svg
					class="!w-6 !h-6"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 4v16m8-8H4"
					></path>
				</svg>
				<span class="font-medium">Create New Field</span>
			</ButtonPrimary>

			<CreateField
				v-if="isCreatingField"
				@field-creation-mode-changed="
					emits('fieldCreationModeChanged', $event)
				"
			/>

			<div
				v-if="isLoadingFields && !isCreatingField"
				class="text-center py-8 text-grey-600"
			>
				<div
					class="animate-spin rounded-full h-6 w-6 border-2 border-green-50 border-b-2 border-b-green-600 mx-auto mb-3"
				></div>
				<div class="text-sm font-medium">Loading fields...</div>
			</div>
			<div
				v-else-if="
					(!fieldsWithAreas || fieldsWithAreas.length === 0) &&
					!isCreatingField
				"
				class="text-center py-4 text-grey-500"
			>
				No fields available
			</div>
			<template v-if="!isLoadingFields && !isCreatingField">
				<button
					v-for="field in fieldsWithAreas"
					:key="field.fieldId"
					:data-field-id="field.fieldId"
					@click="emits('handleFieldClick', field)"
					class="w-full text-left rounded-lg border transition-colors flex items-center gap-3 p-3"
					:class="
						selectedField && selectedField.fieldId === field.fieldId
							? 'border-green-600 bg-green-50 ring-2 ring-green-600 ring-offset-2'
							: 'border-grey-200 hover:border-green-600 hover:bg-green-50'
					"
				>
					<div class="flex-1">
						<div class="font-medium text-grey-950">
							{{ field.fieldName }}
							<div
								v-if="
									selectedField &&
									selectedField.fieldId === field.fieldId
								"
								class="text-green-600 text-sm"
							>
								(Click again to confirm)
							</div>
						</div>
						<div class="text-sm text-grey-500">
							{{ field.calculatedArea }} AC
						</div>
					</div>
					<button
						v-if="
							selectedField &&
							selectedField.fieldId === field.fieldId
						"
						@click.stop="showDeleteDialog(field)"
						class="p-2 text-red-600 border border-red-200 hover:text-red-800 bg-red-50 hover:bg-red-200 rounded-md transition-colors"
						:disabled="farmDeleteMutation.isPending.value"
						title="Delete Field"
					>
						<DeleteIcon class="!w-5 !h-5" />
					</button>
					<PolygonThumbnail
						v-else
						:polygon="field.fieldBoundary"
						:fill="'#10b981'"
						:stroke="'#059669'"
						:fill-opacity="0.2"
						:stroke-width="1.5"
						:size="32"
					/>
				</button>
			</template>
		</div>
	</div>
	<!-- Delete Field Dialog -->
	<Dialog
		ref="deleteDialog"
		title="Delete Field"
		confirm-text="Delete"
		cancel-text="Cancel"
		:loading="farmDeleteMutation.isPending.value"
		@confirm="handleConfirmDelete"
		@cancel="handleCancelDelete"
	>
		<div
			v-if="errorMessage"
			class="mb-2 p-3 bg-red-50 border text-red-600 border-red-200 rounded-md"
		>
			<p class="text-red-700 text-sm">
				Failed to delete field: {{ errorMessage }}
			</p>
		</div>
		<p class="text-grey-700">
			Are you sure you want to delete "{{ fieldToDelete?.fieldName }}"?
			This action cannot be undone.
		</p>
	</Dialog>
</template>

<style scoped>
	.bg-overlay {
		background-color: rgba(0, 0, 0, 0.2);
	}
</style>
